/**
 * @copyright    Copyright © 2019-2025 MiniVentureLab
 * @author       Alok Pabalkar  <alok@miniventurelab.com>
 * @package      Mesh Platform UI
 * @license      Proprietary
 * @description  Console
 * 
 * Cache & Localstorage Get/Set Functionality
 */
import localforage from 'localforage'
import iosBridge from './ios_bridge'
import utility from './utility';
 
localforage.config({
  driver: localforage.IndexedDB, // Force IndexedDB; same as using setDriver()
  name: process.env.VUE_APP_APP_TITLE, //App name
  version: 1.0,
  size: 209715200, // Size of database, in bytes. IndexedDB-only for now.
  storeName: 'meshStore', // Should be alphanumeric, with underscores.
  description: ''
});

var cache = {
  CURRENT_USER_ID: null,

  /**
   * set client device height
   */
  client_height_set_ls(height) {
    if (!window.webkit && !window.androidBridge) {
      return;
    }
    
    let ls_key = "device_ht";
    return localStorage.setItem(ls_key, height);
  },
  /**
   * get client device height
   */
  client_height_get_ls() {
    if (!window.webkit && !window.androidBridge) {
      return;
    }

    let ls_key = "device_ht";
    return localStorage.getItem(ls_key);
  },
  
  /**
   * set_inviteCode_ls
   * set invite code, WITHOUT CURRENT_USER_ID because if the user is new or logging in then CURRENT_USER_ID=null
   */
  async set_inviteCode_ls(invitecode) {
    let ls_key = "invitecode";
    return await localStorage.setItem(ls_key, invitecode);
  },
  /**
   * get_inviteCode_ls
   * get invite code
   */
  async get_inviteCode_ls() {
    let ls_key = "invitecode";
    let invitecode = await localStorage.getItem(ls_key);
    if(invitecode == "null" || invitecode == "false" || !invitecode){
      invitecode = null;
    }
    return invitecode;
  },
  /**
   * setGrpParticipantCount
   */
   setGrpParticipantCount (grpParticipantCountObj) {
    let ls_key = "grp_participant_count_obj"
    localStorage.setItem(ls_key, JSON.stringify(grpParticipantCountObj))
   },
   /**
    * getGrpParticipantCount
    */
    getGrpParticipantCount () {
      let ls_key = "grp_participant_count_obj"
      return localStorage.getItem(ls_key)
    },
  
  /**
    * set_current_user_id_ls
    * Save the currently selected user to Localstorage
    */
  set_current_user_id_ls(CURRENT_USER_ID) {
    let ls_key = "u";
    localStorage.setItem(ls_key, CURRENT_USER_ID);
  },
  /**
    * get_current_user_id_ls
    * Get the currently selected user from Localstorage
    */
  get_current_user_id_ls() {
    let ls_key = "u";
    return localStorage.getItem(ls_key);
  },

  /**
    * set_auth_token_ls
    * Save the token to Localstorage
    * @param token. Dont use save_to_device_storage() as this method does encoding which is not required
    */
  set_auth_token_ls(token) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "user_token";
    localStorage.setItem(ls_key, token);
  },
  /**
    * get_auth_token_ls
    * Get the token from Localstorage
     * @param token. Dont use get_from_device_storage() as this method does encoding which is not required
    */
  get_auth_token_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "user_token";
    return localStorage.getItem(ls_key);
  },

  /**
    * set_user_data_ls
    * Save userdata to Localstorage
    * @param  user_data
    */
  set_user_data_ls(user_data) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "user_data";
    localStorage.setItem(ls_key, JSON.stringify(user_data));
  },
  /**
   * get_user_data_ls
   * Get userdata from Localstorage
   * @param  user_data
   */
  get_user_data_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "user_data";
    return JSON.parse(localStorage.getItem(ls_key))
  },

  /**
   * set_acc_id_ls
   * set acc_id from Localstorage
   * @param  acc_id
   */
  set_acc_id_ls(acc_id) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "acc_id";
    return localStorage.setItem(ls_key, acc_id);
  },
  /**
   * get_acc_id_ls
   * Get acc_id from Localstorage
   */
  get_acc_id_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "acc_id";
    return JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * set_relevant_message_ls
   * set relevant_message to Localstorage
   * @param  relevant_message
   */
  set_relevant_message_ls(relevant_message) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "relevant_message";
    return localStorage.setItem(ls_key, relevant_message);
  },
  /**
   * get_relevant_message_ls()
   * Get relevant_message from Localstorage
   */
  get_relevant_message_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "relevant_message";
    return localStorage.getItem(ls_key);
  },

  /**
   * get_selected_group_ls
   * Get selected_group in group list from Localstorage
   */
  async get_selected_group_ls() {
    if(!cache.CURRENT_USER_ID){
      return;
    }

    let ls_key = cache.CURRENT_USER_ID + "_" + "selected_group";
    let selected_group = await localStorage.getItem(ls_key);
    if (process.env.VUE_APP_DEBUG == "true") {console.log("get_selected_group_ls=>", selected_group);}
    return selected_group;
  },
  /**
   * set_selected_group_ls
   * set selected_group to Localstorage
   * @param  selected_group
   */
  async set_selected_group_ls(selected_group) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "selected_group";
    if (process.env.VUE_APP_DEBUG == "true") {console.log("set_selected_group_ls:", ls_key, selected_group);}

    return await localStorage.setItem(ls_key, selected_group);
  },
  /**
   * set_relevantmsg_list_ls
   *
   */
  set_relevantmsg_list_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "relevantmsg_list";
    var relevantmsg_list = JSON.parse(localStorage.getItem(ls_key));

    if (relevantmsg_list == null || relevantmsg_list == undefined) {
      relevantmsg_list = [];
    }
    relevantmsg_list.push(list);

    localStorage.setItem(ls_key, JSON.stringify(relevantmsg_list));
  },
  /**
   * get_relevantmsg_list_ls
   *
   */
  get_relevantmsg_list_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "relevantmsg_list";
    return JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * set_chatgrp_list_ls
   *
   */
  async set_chatgrp_list_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "chatgrp_list";
    await cache.save_to_device_storage("set_chatgrp_list_ls", ls_key, list, "FS");
  },
  /**
   * get_chatgrp_list_ls
   *
   */
  async get_chatgrp_list_ls() {
    if(!cache.CURRENT_USER_ID){
      return;
    }

    let ls_key = cache.CURRENT_USER_ID + "_" + "chatgrp_list";
    return await cache.get_from_device_storage("get_chatgrp_list_ls", ls_key, "FS");
  },

  /**
  * set_chatgrp_icon_list_ls
  *
  */
  async set_chatgrp_icon_list_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "chatgrp_icon_list";
    await cache.save_to_device_storage("set_chatgrp_icon_list_ls", ls_key, list, "FS");
  },
  /**
   * get_chatgrp_icon_list_ls
   *
   */
  async get_chatgrp_icon_list_ls() {
    if(!cache.CURRENT_USER_ID){
      return;
    }

    let ls_key = cache.CURRENT_USER_ID + "_" + "chatgrp_icon_list";
    return await cache.get_from_device_storage("get_chatgrp_list_ls", ls_key, "FS");
  },

  /**
   * setUserContactList_ls
   *
   */
  async setUserContactList_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "userContactList";
    await cache.save_to_device_storage("setUserContactList_ls", ls_key, list, "FS");
  },
  /**
   * getUserContactList_ls
   *
   */
  async getUserContactList_ls() {
    if(!cache.CURRENT_USER_ID){
      return;
    }

    let ls_key = cache.CURRENT_USER_ID + "_" + "userContactList";
    return await cache.get_from_device_storage("getUserContactList_ls", ls_key, "FS");
  },

  /**
   * set_google_auth_process_loader_ls
   * This methods sets the flag to indicate google login is in progress or stopped. Takes true or false
   */
  set_google_auth_process_loader_ls(loader) {
    return localStorage.setItem("google_auth_process_loader", loader);
  },
  /**
   * get_google_auth_process_loader_ls
   * get google auth process is in processing or not for purpose of show loader
   */
  get_google_auth_process_loader_ls() {
    return localStorage.getItem("google_auth_process_loader");
  },

  /**
   * fcm_push_token_get_ls
   */
  fcm_push_token_get_ls() {
    let ls_key = "fcm_push_token";
    return JSON.parse(localStorage.getItem(ls_key));
  },
  /**
   * fcm_push_token_set_ls
   */
  fcm_push_token_set_ls(fcm_push_token) {
    let ls_key = "fcm_push_token";
    return localStorage.setItem(ls_key, JSON.stringify(fcm_push_token));
  },

  /**
   * set_chatmsg_list_lf
   *
   */
  async set_chatmsg_list_lf(group_id, list) {    
    let ls_key = cache.CURRENT_USER_ID + "_" + "chatmsg_list"+"_"+group_id;

    let chatmsg_list = await cache.get_from_device_storage("set_chatmsg_list_lf", ls_key, "FS")
    if (!chatmsg_list) {
      chatmsg_list = {};
    }
  
    chatmsg_list[group_id] = list;
    await cache.save_to_device_storage("set_chatmsg_list_lf", ls_key, chatmsg_list, "FS");
  },
  /**
   * get_chatmsg_list_lf
   *
   */
  async get_chatmsg_list_lf(group_id) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "chatmsg_list"+"_"+group_id;
    let chatmsg_list = await cache.get_from_device_storage("get_chatmsg_list_lf", ls_key, "FS")
    if (chatmsg_list != null && chatmsg_list != undefined) {
      if (chatmsg_list[group_id]) {
        return chatmsg_list[group_id];
      }
      return false;
    }
    
    return false;  
  },

  /**
  * set_media_data_lf
  * keydate: If date is available then use the data to build the key and set the media list data in the group_date key
  * Else Loop through the list, get the date key and then set the message as per the group and date key
  */
  async set_media_data_lf(group_id, list, date=null) {
    if(!list || Object.keys(list).length <= 0 || !group_id){
      return;
    }
    
    let STORAGE="FS";

    if(date){
      return new Promise(async (resolve) => {
        let ls_key = cache.CURRENT_USER_ID + "_media_data_" + group_id +'_'+ date;
        if (process.env.VUE_APP_DEBUG == "true") {console.log('set_media_data_lf:AddMessage & SocketListen: Save Data for ls_key=',ls_key, ' data =', list);}
                
        let media_data = await cache.get_from_device_storage("set_media_data_lf", ls_key, STORAGE);
        if (!media_data) {
          media_data = [];
        }

        if(Object.keys(media_data).length > 0){
          Object.keys(media_data).map(async index => {
            if(list.mod_obj_id == media_data[index].mod_obj_id){
              //Remove the old element and Push and update the latest
              media_data.splice(index, 1);              
            }
          });
        }        
        media_data.push(list);

        //If size is same then dont write to file or storage
        cache.save_to_device_storage("set_media_data_lf", ls_key, media_data, STORAGE);

        if (process.env.VUE_APP_DEBUG == "true") {console.log('set_media_data_lf:AddMessage & SocketListen: AFTER save_to_device_storage for ls_key=',ls_key, ' data =', media_data);}
        resolve(media_data);
      });
    }
    else{
      if (process.env.VUE_APP_DEBUG == "true") {console.log('set_media_data_lf on ChatMessageList. Get Entire List and set data = ', list);}
      if(Object.keys(list).length > 0){
        let dates = Object.keys(list).map(async date => {
          let ls_key = cache.CURRENT_USER_ID + "_media_data_" + group_id +'_'+ date;

          let media_data = await cache.get_from_device_storage("set_media_data_lf", ls_key, STORAGE);
          let before_size = 0;
          if(media_data && media_data.length > 0){before_size = media_data.length;}
          if (!media_data) {
            //on a New device or after logout and login, for the first time, nothing is found in cache, so make the first entry
            media_data = list[date];
          }
          else{
            await list[date].forEach(async (m)=>{
              let m_index = await media_data.findIndex(md => md.mod_obj_id == m.mod_obj_id);
              if(m_index == '-1'){
                media_data.push(m);
              }
            });
          }
          let after_size = media_data.length;
          
          if(before_size == 0 || before_size != after_size){
            //If size is same then dont write to file or storage
            cache.save_to_device_storage("set_media_data_lf", ls_key, media_data, STORAGE);
            if (process.env.VUE_APP_DEBUG == "true") {console.log('set_media_data_lf Save Data save_to_device_storage for ls_key=',ls_key, 'Size of Object Before write =', before_size, 'Size of Object After write =', after_size, ' data =', media_data);}
          }
          else{
            if (process.env.VUE_APP_DEBUG == "true") {console.log('set_media_data_lf: No Need to duplicate and save to Media List as data already exists.');}
          }
          
          return date;
        });

        await Promise.all(dates);
      }
    }
  },  
  /**
   * get_media_data_lf
   *
   */
  async get_media_data_lf(group_id, date) {
    if(!group_id || !date){
      return;
    }

    let ls_key = cache.CURRENT_USER_ID + "_media_data_" + group_id + '_' + utility.moment(parseInt(date)).format('YYYYMMDD');
    if (process.env.VUE_APP_DEBUG == "true") {console.log('get_media_data_lf',ls_key);}

    let media_data = await cache.get_from_device_storage("get_media_data_lf", ls_key, "FS");
    if (media_data != null && media_data != undefined) {
      if (media_data) {
        return media_data;
      }
      return false;
    }

    return false;
  },

  /**
   * set_app_version_ls
   *
   */
  set_app_version_ls(app_version) {
    let ls_key = "app_version";
    return localStorage.setItem(ls_key, app_version);
  },

  /**
   * get_app_version_ls
   */
  get_app_version_ls() {
    let ls_key = "app_version";
    return localStorage.getItem(ls_key);
  },

  /**
   * set_cookie_acceptance_ls
   *
   */
  set_cookie_acceptance_ls(cookie_acceptance) {
    let ls_key = "cookie_acceptance";
    return localStorage.setItem(ls_key, JSON.stringify(cookie_acceptance));
  },

  /**
   * get_cookie_acceptance_ls
   */
  get_cookie_acceptance_ls() {
    let ls_key = "cookie_acceptance";
    return JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * set_unread_grp_ls
   *
   */
  async set_unread_chatgrp_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "unread_chatgrp_list";
    await localStorage.setItem(ls_key, JSON.stringify(list));
  },

  /**
   * get_unread_grp_ls
   */
  async get_unread_chatgrp_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "unread_chatgrp_list";
    return await JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * set_locked_card_list_ls
   *
   */
  async set_locked_card_list_ls(list) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "locked_card_list";
    await cache.save_to_device_storage("locked_card_list", ls_key, list, "FS");
  },
  /**
   * get_locked_card_list_ls
   *
   */
  get_locked_card_list_ls() {
    let ls_key = cache.CURRENT_USER_ID + "_" + "locked_card_list";
    return cache.get_from_device_storage("locked_card_list", ls_key, "FS");
  },
  /**
   * set_group_participants_lf
   */
   async set_group_participants_lf(groupParticipants) {
    let ls_key = cache.CURRENT_USER_ID + "_" + "group_participant_obj";
    await cache.save_to_device_storage("set_group_participants_lf", ls_key, groupParticipants, "FS");
   },
   /**
    * get_group_participants_lf
    */
    async get_group_participants_lf () {
     let ls_key = cache.CURRENT_USER_ID + "_" + "group_participant_obj";
     const x = await cache.get_from_device_storage("get_group_participants_lf", ls_key, "FS");
     return x
    },
  /**
   * set_selected_card_to_edit_ls
   *
   */
  set_selected_card_to_edit_ls(selected_card_to_edit) {
    let ls_key = "selected_card_to_edit";
    return localStorage.setItem(ls_key, JSON.stringify(selected_card_to_edit));
  },
  /**
   * get_selected_card_to_edit_ls
   */
  get_selected_card_to_edit_ls() {
    let ls_key = "selected_card_to_edit";
    return JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * set_selected_board_ls
   *
   */
  set_selected_board_ls(selected_board) {
    let ls_key = "selected_board";
    return localStorage.setItem(ls_key, JSON.stringify(selected_board));
  },

  /**
   * get_selected_board_ls
   */
  get_selected_board_ls() {
    let ls_key = "selected_board";
    return JSON.parse(localStorage.getItem(ls_key));
  },

  /**
   * save_to_device_storage
   * Save data to device Storage - Shared Preferences [SP] or App File System [FS]
   * storage_type: "SP", "FS"
   */
  async save_to_device_storage(parent_fn, ls_key, data, storage_type="FS") {
    //if (process.env.VUE_APP_DEBUG == "true") {console.log("save_to_device_storage:", parent_fn, ls_key, data);}
    
    data = await btoa(unescape(encodeURIComponent(JSON.stringify(data)))); 
    try {
      if (window.webkit) {
        return new Promise(function (resolve) {
          iosBridge.sendMessage({ "module": "setAppStorageItem", "data": { "storage_type":storage_type, "ls_key": ls_key, "ls_data": data } });
          resolve(true);
        });  
      }
      else if (window.androidBridge) {
        await window.androidBridge.setAppStorageItem(storage_type, ls_key, data);
      }
      else {
        if(storage_type == "SP"){
          await localStorage.setItem(ls_key, data);
        }
        else if(storage_type == "FS"){
          await localforage.setItem(ls_key, data);
        }
      }
    }
    catch (error) {
      console.log("save_to_device_storage SetItem", parent_fn, error);
    }
  },
  /**
   * get_from_device_storage
   * Get data from device Storage - Shared Preferences [SP] or App File System [FS]
   * storage_type: "SP", "FS"
   */
  async get_from_device_storage(parent_fn, ls_key, storage_type) {
    let get_data = null;
    try {
      if (window.webkit) {
        get_data = await iosBridge.sendMessage({ "module": "getAppStorageItem", "data": {"storage_type":storage_type, "ls_key": ls_key} })
        get_data = get_data['data'];
      }
      else if (window.androidBridge) {
        get_data = await window.androidBridge.getAppStorageItem(storage_type, ls_key);
      }
      else {
        if(storage_type == "SP"){
          get_data = await localStorage.getItem(ls_key);
        }
        else{
          get_data = await localforage.getItem(ls_key);
        }
      }
    }
    catch (error) {
      console.log("get_from_device_storage GetItem", parent_fn, error);
    }

    try{
      if(get_data){
        get_data = await JSON.parse(decodeURIComponent(escape(atob(get_data))));
      }
    }
    catch (error) {
      console.log("Error get_from_device_storage, Decode", parent_fn, error);
      get_data = null;
    }
    
    //if (process.env.VUE_APP_DEBUG == "true") {console.log("get_from_device_storage:", parent_fn, ls_key, get_data);}
    
    return get_data;
  },

  /**
    * destroy_data_ls
    * Remove chats_data from Localstorage & IndexDB (localforage)
    */
  destroy_data_ls() {
    console.log("Destroying the database")
    //This is to leave cookie_acceptance from being cleared when user logs out
    //Get cookie_acceptance value
    let cookie_acceptance = null;
    if (!window.webkit && !window.androidBridge) {
      cookie_acceptance = localStorage.getItem('cookie_acceptance');
    }

    localStorage.clear();
    sessionStorage.clear();

    //Clear and Delete meshstore
    localforage.clear();
    localforage.dropInstance({
      name: process.env.VUE_APP_APP_TITLE, //App name
    })
    .then(function () {
      if (process.env.VUE_APP_DEBUG == "true") {console.log('Dropped DB - meshStore, ', process.env.VUE_APP_APP_TITLE);}
    });

    //Set cookie_acceptance value again after logout
    if (!window.webkit && !window.androidBridge) {
      localStorage.setItem('cookie_acceptance',cookie_acceptance);
    }
  },

  /**
   * set_frame parent app name from where the request has originated
   */
  set_frame(frame) {
    let ls_key = "frame";
    return sessionStorage.setItem(ls_key, frame);
  },

  /**
   * get_frame
   */
  get_frame() {
    let ls_key = "frame";
    return sessionStorage.getItem(ls_key);
  }
}/**End of Cache Object */

export default cache;
